/**
 * Tiny client state. Server-state lives in tanstack-query (see hooks).
 */
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { ToastMsg } from '@/design-system/components';
import type { Membership, Tenant, VkUser } from '@/lib/api/v1';

interface ToastStore {
  toasts: ToastMsg[];
  push: (t: Omit<ToastMsg, 'id'>) => void;
  dismiss: (id: string) => void;
}

export const useToasts = create<ToastStore>((set) => ({
  toasts: [],
  push: (t) =>
    set((s) => ({ toasts: [...s.toasts, { id: crypto.randomUUID(), ...t }].slice(-4) })),
  dismiss: (id) => set((s) => ({ toasts: s.toasts.filter((t) => t.id !== id) })),
}));

interface PrintTarget {
  type: 'agent' | 'queue' | 'local';
  agentId?: string;
  agentLabel?: string;
  printerName?: string;
}

interface PrefStore {
  printTarget: PrintTarget | null;
  setPrintTarget: (target: PrintTarget | null) => void;
  defaultLabelLayout: 'duo' | 'single';
  setDefaultLabelLayout: (layout: 'duo' | 'single') => void;
  activeTenantId: string | null;
  setActiveTenantId: (id: string | null) => void;
}

export const usePrefs = create<PrefStore>()(
  persist(
    (set) => ({
      printTarget: null,
      setPrintTarget: (target) => set({ printTarget: target }),
      defaultLabelLayout: 'duo',
      setDefaultLabelLayout: (layout) => set({ defaultLabelLayout: layout }),
      activeTenantId: null,
      setActiveTenantId: (id) => set({ activeTenantId: id }),
    }),
    { name: 'ui-prefs' },
  ),
);

interface SessionStore {
  user: VkUser | null;
  memberships: Membership[];
  tenants: Tenant[];
  /** Auth-context role (ADMIN/OPS/SUPER_ADMIN/USER) — distinct from per-tenant membership role. Used to gate the superadmin plan-override UI in routes/admin.tsx. */
  role: string | null;
  hydrated: boolean;
  setSession: (s: { user: VkUser | null; memberships?: Membership[]; tenants?: Tenant[]; role?: string | null }) => void;
  signOut: () => void;
  setHydrated: (v: boolean) => void;
}

export const useSession = create<SessionStore>()(
  persist(
    (set) => ({
      user: null,
      memberships: [],
      tenants: [],
      role: null,
      hydrated: false,
      setSession: (s) =>
        set((prev) => ({
          user: s.user,
          memberships: s.memberships ?? prev.memberships,
          tenants: s.tenants ?? prev.tenants,
          role: s.role !== undefined ? s.role : prev.role,
        })),
      signOut: () => set({ user: null, memberships: [], tenants: [], role: null }),
      setHydrated: (v) => set({ hydrated: v }),
    }),
    {
      name: 'ui-session',
      version: 3,
      migrate: (state) => {
        const persisted = state as Partial<SessionStore> | undefined;
        if (persisted?.user?.email === 'dev@vin.invested.no' || persisted?.user?.id === 'dev-user') {
          return { ...persisted, user: null, memberships: [], tenants: [], role: null, hydrated: false };
        }
        return persisted ?? { user: null, memberships: [], tenants: [], role: null, hydrated: false };
      },
    },
  ),
);
